import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createService, createStaff } from "../lib/api.ts";

interface ServiceRow {
  name: string;
  duration_min: number;
  price_cents: number;
}
interface StaffRow {
  name: string;
  email: string;
}

const emptyService: ServiceRow = { name: "", duration_min: 30, price_cents: 0 };
const emptyStaff: StaffRow = { name: "", email: "" };

export default function Onboarding() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<"services" | "staff">("services");
  const [services, setServices] = useState<ServiceRow[]>([
    { ...emptyService },
    { ...emptyService },
    { ...emptyService },
  ]);
  const [staff, setStaff] = useState<StaffRow[]>([
    { ...emptyStaff },
    { ...emptyStaff },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateService = (i: number, patch: Partial<ServiceRow>) =>
    setServices((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const updateStaff = (i: number, patch: Partial<StaffRow>) =>
    setStaff((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const submitServices = async () => {
    const filled = services.filter((s) => s.name.trim().length > 0);
    if (filled.length === 0) {
      setStep("staff");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      for (const s of filled) {
        await createService(slug, {
          name: s.name.trim(),
          duration_min: s.duration_min,
          price_cents: s.price_cents,
        });
      }
      setStep("staff");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitStaff = async () => {
    const filled = staff.filter((s) => s.name.trim().length > 0);
    if (filled.length === 0) {
      navigate(`/${slug}`, { replace: true });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      for (const s of filled) {
        await createStaff(slug, {
          name: s.name.trim(),
          email: s.email.trim() || null,
        });
      }
      navigate(`/${slug}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const skipAll = () => navigate(`/${slug}`, { replace: true });

  return (
    <div className="min-h-screen" style={{ background: "var(--canvas)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 24px 80px" }}>
        <div
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10.5,
            fontWeight: 500,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--ink-faint)",
            marginBottom: 12,
          }}
        >
          Setup · Step {step === "services" ? "1" : "2"} of 2
        </div>
        <h1
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontWeight: 500,
            fontSize: 36,
            letterSpacing: "-0.018em",
            color: "var(--ink)",
            margin: "0 0 8px",
          }}
        >
          {step === "services" ? "Add a few services." : "Add the team."}
        </h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55, margin: "0 0 32px", maxWidth: "60ch" }}>
          {step === "services"
            ? "Services are what customers book — name, duration, price. Skip rows you don't need; you can always add more from the Services page later."
            : "Staff are the people who take appointments. Even just yourself for now — you can invite more from the Staff page anytime."}
        </p>

        {step === "services" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {services.map((s, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-2.5 items-end">
                <div>
                  <label className="label">Name</label>
                  <input
                    className="input"
                    value={s.name}
                    onChange={(e) => updateService(i, { name: e.target.value })}
                    placeholder={["e.g. New patient consultation", "e.g. Adjustment", "e.g. 60-min massage"][i]}
                  />
                </div>
                <div>
                  <label className="label">Duration (min)</label>
                  <input
                    className="input"
                    type="number"
                    min={5}
                    step={5}
                    value={s.duration_min}
                    onChange={(e) => updateService(i, { duration_min: Number(e.target.value) || 30 })}
                  />
                </div>
                <div>
                  <label className="label">Price ($)</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={s.price_cents / 100}
                    onChange={(e) => updateService(i, { price_cents: Math.round(Number(e.target.value) * 100) })}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {step === "staff" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {staff.map((s, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 items-end">
                <div>
                  <label className="label">Name</label>
                  <input
                    className="input"
                    value={s.name}
                    onChange={(e) => updateStaff(i, { name: e.target.value })}
                    placeholder={["e.g. Dr. Marcus Hayes", "e.g. Sofia Reyes, LMT"][i]}
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    className="input"
                    type="email"
                    value={s.email}
                    onChange={(e) => updateStaff(i, { email: e.target.value })}
                    placeholder="optional"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="alert-error" style={{ marginTop: 16, fontSize: 13.5 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 28, alignItems: "center" }}>
          <button
            type="button"
            className="zd-hero-cta"
            onClick={step === "services" ? submitServices : submitStaff}
            disabled={submitting}
            style={{ opacity: submitting ? 0.4 : 1 }}
          >
            {submitting ? "Saving…" : step === "services" ? "Continue →" : "Finish setup →"}
          </button>
          <button
            type="button"
            onClick={step === "services" ? () => setStep("staff") : skipAll}
            style={{
              background: "transparent",
              border: "1px solid var(--rule)",
              borderRadius: 10,
              padding: "0 18px",
              height: 44,
              fontSize: 13.5,
              color: "var(--ink-soft)",
              cursor: "pointer",
            }}
          >
            {step === "services" ? "Skip for now" : "Skip and finish"}
          </button>
        </div>
      </div>
    </div>
  );
}
